import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { AudioAssetDto } from "../api/types";
import { formatTime } from "../lib/format";
import { FileChoiceDialog } from "./FileChoiceDialog";
import { toast } from "./toast";

/** Files uploaded but not queued for proofing - the ones somebody deliberately
 *  left out while they were arriving. */
export function parkedFiles(assets: AudioAssetDto[]): AudioAssetDto[] {
  return assets.filter((a) => a.proof_requested === false);
}

/**
 * Starting proofing on files that were only uploaded.
 *
 * There is an older endpoint that opts EVERY parked file in at once. That is
 * right for recovering a stalled run and wrong as a way to start one: a file is
 * parked because somebody said so while it was uploading, and sweeping it back
 * in would quietly overrule them. So this asks - over the same list, with the
 * same ticking, as the dialog that parked them in the first place.
 */
export function StartProofingDialog({ projectId, files, onClose, onStarted }: {
  projectId: string;
  files: AudioAssetDto[];
  onClose: () => void;
  onStarted?: () => void;
}) {
  const qc = useQueryClient();
  const start = useMutation({
    mutationFn: (names: string[]) =>
      api<{ started: string[] }>(`/api/projects/${projectId}/proof/start`, {
        method: "POST", body: JSON.stringify({ filenames: names }),
      }),
    onSuccess: (r, names) => {
      const n = r.started.length || names.length;
      toast(`Proofing ${n} file${n === 1 ? "" : "s"} — they transcribe and proof in the background.`, "success");
      qc.invalidateQueries({ queryKey: ["audio", projectId] });
      qc.invalidateQueries({ queryKey: ["project", projectId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      onClose();
      onStarted?.();
    },
    onError: (e: any) => toast(e.message || "Couldn't start proofing.", "error"),
  });

  return (
    <FileChoiceDialog
      title="Start proofing on which files?"
      lede={<>
        These were uploaded without proofing. A ticked file is transcribed and compared
        against the manuscript now; an unticked one stays as it is.
      </>}
      items={files.map((a) => ({
        name: a.filename,
        note: a.duration_seconds ? formatTime(a.duration_seconds) : "",
      }))}
      tagOn="Proof now" tagOff="Leave"
      confirmLabel={(n) => (n === 0 ? "Start proofing" : `Start proofing on ${n} file${n === 1 ? "" : "s"}`)}
      summary={(n, rest) =>
        n === 0 ? "Nothing selected - tick the files you want proofed."
          : rest === 0 ? `All ${n} will start now.`
            : `${n} will start now; ${rest} left as ${rest === 1 ? "it is" : "they are"}.`}
      onCancel={onClose}
      onConfirm={(picked) => {
        const names = files.map((a) => a.filename).filter((n) => picked.has(n));
        if (!names.length) { toast("Tick the files you want proofed.", "error"); return; }
        start.mutate(names);
      }}
    />
  );
}
